import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function HospitalAdmissionsModal({ serverId, serviceUserId, onClose }) {
    const [admissions, setAdmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchAdmissions = async () => {
            setLoading(true);
            try {
                const ref = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'hospitalAdmissions');
                const snap = await getDocs(query(ref));
                
                const loaded = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                loaded.sort((a, b) => {
                    const getMs = (val) => {
                        if (!val) return 0;
                        if (typeof val.toDate === 'function') return val.toDate().getTime();
                        if (val.seconds) return val.seconds * 1000;
                        return new Date(val).getTime() || 0;
                    };
                    const dateA = getMs(a.admissionDate || a.createdAt || a.date);
                    const dateB = getMs(b.admissionDate || b.createdAt || b.date);
                    return dateB - dateA; // Newest first
                });
                
                setAdmissions(loaded);
            } catch (err) {
                console.error("Error fetching hospital admissions:", err);
                setError("Failed to load hospital admissions.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId) {
            fetchAdmissions();
        }
    }, [serverId, serviceUserId]);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Hospital Admissions</h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>Record of hospital stays and discharge summaries</p>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading admissions...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : admissions.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚑</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Admissions</h3>
                            <p style={{ margin: 0, color: '#6b7280' }}>There are no hospital admissions recorded for this service user.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {admissions.map((admission, index) => {
                                const hospitalName = admission.hospitalName || admission.name || 'Unknown Hospital';
                                const reason = admission.reason || admission.reasonForAdmission || 'Reason not specified';
                                const isCurrentlyAdmitted = !admission.dischargeDate && admission.status !== 'discharged';
                                
                                return (
                                    <div key={admission.id || index} style={styles.recordCard}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>{hospitalName}</h3>
                                                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                                    {reason}
                                                </div>
                                            </div>
                                            <span style={{ 
                                                display: 'inline-block', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 'bold',
                                                backgroundColor: isCurrentlyAdmitted ? '#fee2e2' : '#f3f4f6', 
                                                color: isCurrentlyAdmitted ? '#991b1b' : '#6b7280', 
                                                border: `1px solid ${isCurrentlyAdmitted ? '#fecaca' : '#d1d5db'}`
                                            }}>
                                                {isCurrentlyAdmitted ? 'CURRENTLY ADMITTED' : 'DISCHARGED'}
                                            </span>
                                        </div>
                                        
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Admission Date</label>
                                                <div style={styles.fieldValue}>{admission.admissionDate || admission.createdAt || admission.date ? new Date((admission.admissionDate || admission.createdAt || admission.date)?.seconds ? (admission.admissionDate || admission.createdAt || admission.date).seconds * 1000 : (admission.admissionDate || admission.createdAt || admission.date)).toLocaleDateString() : 'N/A'}</div>
                                            </div>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Discharge Date</label>
                                                <div style={styles.fieldValue}>{admission.dischargeDate ? new Date(admission.dischargeDate?.seconds ? admission.dischargeDate.seconds * 1000 : admission.dischargeDate).toLocaleDateString() : (isCurrentlyAdmitted ? 'Pending' : 'Unknown')}</div>
                                            </div>
                                        </div>
                                        
                                        {(admission.dischargeSummary || admission.notes) && (
                                            <div style={{ marginTop: '16px' }}>
                                                <label style={styles.fieldLabel}>Discharge Summary / Notes</label>
                                                <div style={{ fontSize: '13px', color: '#4b5563' }}>{admission.dischargeSummary || admission.notes}</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    fieldLabel: { display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', marginBottom: '4px', fontWeight: 'bold' },
    fieldValue: { fontSize: '14px', color: '#111827' }
};
